import { useState } from 'react';

const COMP_STEPS = [
  'Scanning the local market...',
  'Identifying competing concepts...',
  'Pulling competitor details...',
];

const MARKET_STEPS = [
  'Looking up competitors on Google...',
  'Pulling real review scores...',
  'Analyzing competitive positioning...',
  'Building pricing benchmarks...',
  'Pulling industry averages...',
  'Evaluating menu and beverage trends...',
  'Assessing demand signals...',
];

const AUDIT_STEPS = [
  'Pulling Google reviews...',
  'Scraping Instagram profile...',
  'Checking OpenTable listings...',
  'Checking Resy listings...',
  'Reading uploaded files...',
  'Analyzing website and digital presence...',
  'Processing guest review sentiment...',
  'Evaluating menu structure and pricing...',
  'Assessing social media metrics...',
  'Analyzing beverage program...',
  'Evaluating emotional experience...',
  'Mapping revenue leaks...',
  'Scoring against market baseline...',
  'Building the Next Experience Roadmap...',
  'Finalizing Next Index Score...',
];

function ProgressAnimation({ steps, currentStep, title, subtitle }) {
  return (
    <div className="card p-8 text-center">
      <div className="mb-6">
        <div className="w-16 h-16 rounded-full bg-brand-gold/20 flex items-center justify-center mx-auto mb-4 animate-pulse-ring">
          <span className="text-brand-gold text-2xl">AI</span>
        </div>
        <h3 className="font-display text-xl text-white mb-2">{title}</h3>
        <p className="text-neutral-400 text-sm">{subtitle}</p>
      </div>
      <div className="space-y-2 max-w-md mx-auto text-left">
        {steps.map((step, i) => (
          <div key={i} className={`flex items-center gap-3 text-sm transition-all duration-500 ${i < currentStep ? 'text-brand-gold' : i === currentStep ? 'text-white' : 'text-neutral-700'}`}>
            <span className="w-5 flex-shrink-0">
              {i < currentStep ? '✓' : i === currentStep ? <span className="inline-block w-2 h-2 rounded-full bg-brand-gold animate-pulse"></span> : '·'}
            </span>
            {step}
          </div>
        ))}
      </div>
      <div className="mt-6">
        <div className="h-2 bg-neutral-800 rounded-full overflow-hidden max-w-xs mx-auto">
          <div className="h-full bg-brand-gold rounded-full transition-all duration-1000 ease-out" style={{ width: `${Math.min(95, Math.round(((currentStep + 1) / steps.length) * 100))}%` }} />
        </div>
        <p className="text-xs text-neutral-500 text-center mt-2">{Math.min(95, Math.round(((currentStep + 1) / steps.length) * 100))}% complete</p>
      </div>
    </div>
  );
}

function CompetitorList({ competitors, onApprove, onFindMore, savedCompetitors }) {
  const [comps, setComps] = useState(Array.isArray(competitors) ? competitors : []);
  const [saved, setSaved] = useState(Array.isArray(savedCompetitors) ? savedCompetitors.map(c => c?.name || c).filter(Boolean) : []);
  const [removed, setRemoved] = useState([]);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('');

  const toggleSave = (name) => {
    setSaved(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const removeComp = (index) => {
    const comp = comps[index];
    setRemoved(prev => [...prev, { name: comp.name, reason: '' }]);
    setComps(comps.filter((_, i) => i !== index));
  };

  const updateRemoveReason = (name, reason) => {
    setRemoved(prev => prev.map(r => r.name === name ? { ...r, reason } : r));
  };

  const addComp = () => {
    if (newName.trim()) {
      const newComp = { name: newName.trim(), type: newType.trim() || 'Restaurant', priceRange: '', strengths: '', weaknesses: '', manuallyAdded: true };
      setComps([...comps, newComp]);
      setSaved([...saved, newName.trim()]);
      setNewName('');
      setNewType('');
    }
  };

  const handleFindMore = () => {
    const savedComps = comps.filter(c => saved.includes(c.name));
    const excludeNotes = removed.filter(r => r.reason).map(r => `${r.name}: ${r.reason}`).join('; ');
    if (onFindMore) onFindMore(savedComps, excludeNotes);
  };

  return (
    <div className="card p-6">
      <h3 className="font-display text-lg text-white mb-2">Review Competitor Set</h3>
      <p className="text-neutral-400 text-sm mb-4">Lock competitors to keep, remove ones that don't fit, add your own.</p>

      <div className="space-y-2 mb-4">
        {comps.map((comp, i) => {
          const isSaved = saved.includes(comp.name);
          return (
            <div key={i} className={`flex items-center gap-3 rounded-lg px-4 py-3 border transition-all ${isSaved ? 'bg-brand-gold/5 border-brand-gold/30' : 'bg-neutral-800/50 border-neutral-700/30'}`}>
              <button onClick={() => toggleSave(comp.name)} className={`text-sm ${isSaved ? 'text-brand-gold' : 'text-neutral-600'}`} title={isSaved ? 'Saved — click to unlock' : 'Click to save/lock'}>
                {isSaved ? '🔒' : '🔓'}
              </button>
              <div className="flex-1">
                <p className="text-white text-sm font-medium">{comp.name} {comp.manuallyAdded && <span className="text-[9px] text-brand-gold">(added by you)</span>}</p>
                <p className="text-neutral-500 text-xs">{comp.type}{comp.priceRange ? ` · ${comp.priceRange}` : ''}</p>
              </div>
              {isSaved ? (
                <span className="text-[9px] text-brand-gold">Locked</span>
              ) : (
                <button onClick={() => removeComp(i)} className="text-neutral-500 hover:text-red-400 text-xs transition-colors">Remove</button>
              )}
            </div>
          );
        })}
      </div>

      {/* Removed competitors with reason */}
      {removed.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] text-neutral-600 mb-2">Removed (AI will avoid these next time):</p>
          {removed.map((r, i) => (
            <div key={i} className="flex items-center gap-2 mb-1">
              <span className="text-xs text-neutral-600 line-through">{r.name}</span>
              <input
                type="text"
                value={r.reason}
                onChange={(e) => updateRemoveReason(r.name, e.target.value)}
                placeholder="Why not a competitor? (optional)"
                className="input-field text-[10px] py-1 flex-1"
              />
            </div>
          ))}
        </div>
      )}

      {/* Add competitor */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Competitor name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className="input-field text-sm flex-1"
          onKeyDown={(e) => e.key === 'Enter' && addComp()}
        />
        <input
          type="text"
          placeholder="Type (e.g., Bar)"
          value={newType}
          onChange={(e) => setNewType(e.target.value)}
          className="input-field text-sm w-36"
          onKeyDown={(e) => e.key === 'Enter' && addComp()}
        />
        <button onClick={addComp} className="btn-ghost text-sm">+ Add</button>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button onClick={() => onApprove(comps)} className="btn-primary flex-1" disabled={comps.length === 0}>
          Approve {comps.length} Competitor{comps.length !== 1 ? 's' : ''}
        </button>
        <button onClick={handleFindMore} className="btn-secondary text-xs">
          Find {Math.max(0, 10 - saved.length)} More
        </button>
      </div>
    </div>
  );
}

export default function AIResearchButton({ intake, existingScores, marketData, approvedCompetitors, aiResearch: audit_aiResearch, onCompetitorsFound, onCompetitorsApproved, onMarketComplete, onAuditComplete }) {
  const [activeStep, setActiveStep] = useState(null); // null, 'findComps', 'market', 'audit'
  const [currentProgress, setCurrentProgress] = useState(0);
  const [foundCompetitors, setFoundCompetitors] = useState(null);
  const [error, setError] = useState('');

  const callClaude = async (messages, maxTokens = 4000, images = []) => {
    // If images provided, build multimodal content
    if (images.length > 0 && messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      const content = [
        ...images.map(img => ({
          type: 'image',
          source: { type: 'base64', media_type: img.mediaType, data: img.data },
        })),
        { type: 'text', text: lastMsg.content },
      ];
      messages = [...messages.slice(0, -1), { role: lastMsg.role, content }];
    }
    const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
    if (!apiKey) throw new Error('Anthropic API key not configured. Add VITE_ANTHROPIC_API_KEY to environment variables.');

    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model: 'claude-sonnet-4-6',
        max_tokens: maxTokens,
        messages,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      console.error('Claude API error:', err);
      throw new Error(`Claude API error: ${response.status} — ${err?.error?.message || 'Unknown error'}`);
    }

    const data = await response.json();
    const rawText = data?.content?.[0]?.text || '';
    console.log('Claude response length:', rawText.length);
    console.log('Claude stop reason:', data?.stop_reason);

    const jsonMatch = rawText.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.error('No JSON found in response:', rawText.substring(0, 500));
      throw new Error('Could not parse AI response.');
    }

    try {
      return JSON.parse(jsonMatch[0]);
    } catch (e) {
      // Try to repair truncated JSON by closing open brackets/braces
      console.warn('JSON parse failed, attempting repair...');
      let repaired = jsonMatch[0];
      // Count open vs close braces and brackets
      const openBraces = (repaired.match(/\{/g) || []).length;
      const closeBraces = (repaired.match(/\}/g) || []).length;
      const openBrackets = (repaired.match(/\[/g) || []).length;
      const closeBrackets = (repaired.match(/\]/g) || []).length;
      // Remove any trailing partial content after last complete value
      repaired = repaired.replace(/,\s*"[^"]*"?\s*$/, '');
      repaired = repaired.replace(/,\s*\[?\s*$/, '');
      repaired = repaired.replace(/,\s*$/, '');
      // Close open brackets and braces
      for (let i = 0; i < openBrackets - closeBrackets; i++) repaired += ']';
      for (let i = 0; i < openBraces - closeBraces; i++) repaired += '}';
      try {
        return JSON.parse(repaired);
      } catch (e2) {
        console.error('JSON repair also failed:', e2);
        throw new Error('AI response was truncated. Try running the audit again.');
      }
    }
  };

  const runWithProgress = async (steps, fn) => {
    setCurrentProgress(0);
    // Advance steps but stop 2 before the end — last steps only show when API responds
    const interval = setInterval(() => {
      setCurrentProgress(prev => prev < steps.length - 2 ? prev + 1 : prev);
    }, 4000);
    try {
      const result = await fn();
      clearInterval(interval);
      return result;
    } catch (err) {
      clearInterval(interval);
      throw err;
    }
  };

  // STEP 1: Find competitors
  const [savedComps, setSavedComps] = useState([]);
  const [excludeNotes, setExcludeNotes] = useState('');

  const findCompetitors = async (keepSaved = null, excludeInfo = '') => {
    setActiveStep('findComps');
    setError('');
    if (keepSaved) setSavedComps(keepSaved);
    if (excludeInfo) setExcludeNotes(excludeInfo);

    const safeSaved = Array.isArray(keepSaved) ? keepSaved : Array.isArray(savedComps) ? savedComps : [];
    const savedNames = safeSaved.map(c => c?.name || '').filter(Boolean);
    const slotsNeeded = Math.max(1, 10 - savedNames.length);

    try {
      const result = await runWithProgress(COMP_STEPS, () => callClaude([{
        role: 'user',
        content: `You are a hospitality market analyst. Find ${slotsNeeded > 0 ? slotsNeeded : 5} direct competitors for this concept:\n\nConcept: ${intake.conceptName || 'Unknown'}\nType: ${intake.conceptType || 'Unknown'}\nCity/Market: ${intake.city || 'Unknown'}\nWebsite: ${intake.website || 'N/A'}${savedNames.length > 0 ? `\n\nALREADY SELECTED (do not include these again): ${savedNames.join(', ')}` : ''}${excludeInfo || excludeNotes ? `\n\nEXCLUDED (do not suggest these, here's why): ${excludeInfo || excludeNotes}` : ''}\n\nReturn ONLY this JSON:\n{"competitors":[{"name":"","type":"","priceRange":"","strengths":"short note","weaknesses":"short note"}]}\n\nBe specific with real businesses in the area. Return ONLY valid JSON.`,
      }], 2000));

      const newComps = result.competitors || [];
      const merged = [...(keepSaved || savedComps || []), ...newComps];
      setFoundCompetitors(merged);
      if (onCompetitorsFound) onCompetitorsFound(merged);
      setActiveStep('review');
    } catch (err) {
      setError(err.message);
      setActiveStep(null);
    }
  };

  // STEP 2: Research market with approved competitors
  const researchMarket = async (approvedComps = null) => {
    const compsToUse = approvedComps || approvedCompetitors || [];
    if (compsToUse.length === 0) { setError('Approve competitors first (Step 1)'); return; }
    setActiveStep('market');
    setError('');
    try {
      const result = await runWithProgress(MARKET_STEPS, async () => {
        // Pull real Google data + review texts for each competitor
        let competitorGoogleData = '';
        try {
          const { getGoogleReviewData } = await import('../utils/reviewScraper');
          const compResults = await Promise.allSettled(
            compsToUse.slice(0, 10).map(c => getGoogleReviewData(c.name, intake.city))
          );
          compResults.forEach((r, i) => {
            if (r.status === 'fulfilled' && r.value?.found) {
              const d = r.value;
              competitorGoogleData += `\n\n### ${d.name}`;
              competitorGoogleData += `\nRating: ${d.rating}/5 (${d.totalReviews} reviews)`;
              if (d.priceLevel) competitorGoogleData += ` | Price: ${'$'.repeat(d.priceLevel)}`;
              if (d.website) competitorGoogleData += `\nWebsite: ${d.website}`;
              // Include actual review texts
              if (d.reviews && d.reviews.length > 0) {
                competitorGoogleData += `\nRecent Reviews:`;
                d.reviews.forEach(rev => {
                  competitorGoogleData += `\n- ${rev.rating}★ (${rev.time}): "${rev.text.substring(0, 300)}${rev.text.length > 300 ? '...' : ''}"`;
                });
              }
              compsToUse[i].realGoogleRating = d.rating;
              compsToUse[i].realReviewCount = d.totalReviews;
              compsToUse[i].realPriceLevel = d.priceLevel;
              console.log(`Competitor Google data: ${d.name} — ${d.rating}/5 (${d.totalReviews}) + ${d.reviews?.length || 0} review texts`);
            }
          });
        } catch (err) {
          console.warn('Competitor Google lookup failed:', err.message);
        }

        return callClaude([{
          role: 'user',
          content: `You are a hospitality market intelligence analyst for Next Experience Group.\n\nResearch the market for: ${intake.conceptName || 'Restaurant'} in ${intake.city || 'Unknown'}\nWebsite: ${intake.website || 'N/A'}\nInstagram: ${intake.instagram || 'N/A'}\n\nAPPROVED COMPETITORS TO ANALYZE:\n${compsToUse.map(c => `- ${c.name} (${c.type})${c.realGoogleRating ? ` [REAL Google: ${c.realGoogleRating}/5, ${c.realReviewCount} reviews]` : ''}`).join('\n')}${competitorGoogleData ? '\n\nREAL GOOGLE DATA FOR COMPETITORS:' + competitorGoogleData : ''}\n\nFor EACH competitor, use the REAL Google reviews and ratings above. Analyze their actual review texts for strengths and weaknesses. Research their menu pricing (appetizer avg, entree avg, cocktail avg, wine markup) and competitive positioning. Base your analysis on the real reviews provided — do not infer when real data is available.\n\nReturn this JSON:\n{"marketOverview":"","competitors":[{"name":"","type":"","priceRange":"","strengths":"","weaknesses":"","reviewScore":0,"totalReviews":0,"estimatedRevenue":"","menuPricing":{"appetizers":"","entrees":"","cocktails":"","wine":""},"instagram":{"followers":0,"engagement":"","postingFrequency":"","contentQuality":""}}],"industryAverages":{"avgReviewScore":0,"avgPricePoint":"","avgMenuSize":"","avgBeverageAttachment":"","avgSocialFollowers":0,"avgEngagementRate":""},"subjectInstagram":{"followers":0,"engagement":"","postingFrequency":"","contentQuality":"","guestTagging":"","hashtagStrategy":""},"marketTrends":[],"menuTrends":{"food":[],"beverage":[]},"menuPriceComparison":{"subject":{"appetizers":"","entrees":"","cocktails":""},"competitorAvg":{"appetizers":"","entrees":"","cocktails":""},"opportunity":""},"demandSignals":{"strength":"","notes":""},"pricingBenchmark":{"low":"","mid":"","high":""},"marketGaps":[],"competitiveThreats":[],"confidence":""}\n\nIMPORTANT: Use the REAL Google review scores provided above for competitor reviewScore fields. Return ONLY valid JSON.`,
        }], 8000);
      });

      onMarketComplete(result);
      setActiveStep(null);
    } catch (err) {
      setError(err.message);
      setActiveStep(null);
    }
  };

  // STEP 3: Audit the business
  const runAudit = async () => {
    setActiveStep('audit');
    setError('');
    try {
      const result = await runWithProgress(AUDIT_STEPS, async () => {
        // Pull real data from multiple sources before calling AI
        let googleReviews = '';
        let scrapedData = '';
        let urlContent = '';

        // Fetch any URLs found in text fields
        try {
          const { processUrlsInText } = await import('../utils/urlFetcher');
          const allText = [intake.menuNotes, intake.financialSnapshot, intake.deckNotes, intake.additionalNotes, intake.website].filter(Boolean).join(' ');
          urlContent = await processUrlsInText(allText);
          if (urlContent) console.log('URL content fetched for AI');
        } catch (err) {
          console.warn('URL fetching skipped:', err.message);
        }

        // Google Reviews
        try {
          const { getGoogleReviewData, formatReviewsForAI } = await import('../utils/reviewScraper');
          const googleData = await getGoogleReviewData(intake.conceptName, intake.city);
          if (googleData?.found) {
            googleReviews = formatReviewsForAI(googleData);
            console.log('Google reviews pulled:', googleData.totalReviews, 'reviews');
          }
        } catch (err) {
          console.warn('Google reviews not available:', err.message);
        }

        // Instagram, OpenTable, Resy via Apify
        try {
          const { scrapeInstagram, scrapeOpenTable, scrapeResy, formatScrapedDataForAI } = await import('../utils/apifyScraper');

          // Run all scrapes in parallel
          const [instaData, otData, resyData] = await Promise.allSettled([
            intake.instagram ? scrapeInstagram(intake.instagram) : Promise.resolve(null),
            scrapeOpenTable(intake.conceptName, intake.city),
            scrapeResy(intake.conceptName, intake.city),
          ]);

          const instagram = instaData.status === 'fulfilled' ? instaData.value : null;
          const opentable = otData.status === 'fulfilled' ? otData.value : null;
          const resy = resyData.status === 'fulfilled' ? resyData.value : null;

          scrapedData = formatScrapedDataForAI(instagram, opentable, resy);

          if (instagram?.found) console.log('Instagram scraped:', instagram.followers, 'followers');
          if (opentable?.found) console.log('OpenTable data found');
          if (resy?.found) console.log('Resy data found');
        } catch (err) {
          console.warn('Apify scraping not available:', err.message);
        }

        // Extract text from uploaded files
        let fileContent = '';
        let fileImages = [];
        try {
          const { extractFileContent, formatFilesForAI } = await import('../utils/fileReader');
          const files = intake.files || {};
          const extracted = await Promise.all(
            Object.values(files).filter(Boolean).map(f => extractFileContent(f))
          );
          const formatted = formatFilesForAI(extracted.filter(Boolean));
          fileContent = formatted.textContent;
          fileImages = formatted.images;
          if (fileContent) console.log('File content extracted for AI');
          if (fileImages.length) console.log(`${fileImages.length} images extracted for AI vision`);
        } catch (err) {
          console.warn('File extraction skipped:', err.message);
        }

        let msg = `You are Next Experience Audit by Next Experience Group. Evaluate this hospitality business. Score 10 pillars 1.0-10.0: Concept Clarity (15%), Brand Expression (15%), Guest Signal (15%), Operational Confidence (15%), Demand Generation (10%), Commercial Performance (15%), Growth Readiness (15%), Emotional Experience, Menu & Ingredient Intelligence, Beverage Program Intelligence.\n\nConcept: ${intake.conceptName || 'Unknown'}\nConcept Type: ${intake.conceptType || 'Unknown'}\nCity: ${intake.city || 'Unknown'}\nWebsite: ${intake.website || 'N/A'}\nInstagram: ${intake.instagram || 'N/A'}\n\nIMPORTANT: Also pull and include in your analysis the business's Google Maps listing details (address, phone number, hours of operation, Google rating, review count, photos count). Include these in your research.`;
        if (intake.financialSnapshot) msg += `\n\nFinancials:\n${intake.financialSnapshot}`;
        if (intake.menuNotes) msg += `\n\nMenu:\n${intake.menuNotes}`;
        if (intake.deckNotes) msg += `\n\nDeck:\n${intake.deckNotes}`;

        // Add extracted file content
        if (fileContent) msg += fileContent;
        // Add fetched URL content (menus, websites, etc.)
        if (urlContent) msg += `\n\n**FETCHED WEB CONTENT (from URLs in notes):**${urlContent}`;
        if (intake.additionalNotes) msg += `\n\nNotes:\n${intake.additionalNotes}`;

        // Add real data from all sources
        if (googleReviews) msg += googleReviews;
        if (scrapedData) msg += scrapedData;

        if (existingScores && Object.keys(existingScores).length > 0) {
          msg += `\n\nTeam scores (enhance these):`;
          for (const [k, v] of Object.entries(existingScores)) {
            if (v && v.score > 0) msg += `\n- ${k}: ${v.score}/10 "${v.rationale || ''}"`;
          }
        }
        if (marketData) {
          msg += `\n\nMARKET BASELINE:\nCompetitors: ${JSON.stringify(marketData.competitors)}\nAverages: ${JSON.stringify(marketData.industryAverages)}\nTrends: ${JSON.stringify(marketData.menuTrends)}`;
        }
        msg += `\n\nIMPORTANT: Use the REAL data above (Google reviews, Instagram metrics, OpenTable, Resy) for scoring. Break sentiment out BY SOURCE. For emotionalWords and transactionalWords, return SINGLE WORDS only (e.g., "amazing", "cozy", "romantic", "decent", "fine", "overpriced") — NOT sentences or phrases. These are used for a word cloud. Score based on REAL evidence.\n\nSCORING RULES:\n- memoryTest.score: 1-10 based on how many memorable moments exist vs gaps\n- socialProof: score each metric 1-10 (posting_consistency, engagement_rate, content_quality, guest_tagging, hashtag_performance)\n- brandCoherence._grid: for each channel x dimension, use 'aligned', 'partial', or 'fractured' based on evidence. If unknown, use 'partial'\n\nReturn ONLY this JSON: {"overallConfidence":"","executiveDiagnostic":"","businessInOnePage":"","scores":{"conceptClarity":{"score":0,"rationale":"","evidence":"","confidence":""},"brandExpression":{"score":0,"rationale":"","evidence":"","confidence":""},"guestSignal":{"score":0,"rationale":"","evidence":"","confidence":""},"operationalConfidence":{"score":0,"rationale":"","evidence":"","confidence":""},"demandGeneration":{"score":0,"rationale":"","evidence":"","confidence":""},"commercialPerformance":{"score":0,"rationale":"","evidence":"","confidence":""},"growthReadiness":{"score":0,"rationale":"","evidence":"","confidence":""},"emotionalExperience":{"score":0,"rationale":"","evidence":"","confidence":""},"menuIntelligence":{"score":0,"rationale":"","evidence":"","confidence":""},"beverageIntelligence":{"score":0,"rationale":"","evidence":"","confidence":""}},"scoreTensions":[],"memoryTest":{"score":0,"signatureMoments":[],"gaps":[]},"revenueLeaks":[{"area":"","issue":"","impact":"High|Medium|Low","fix":""}],"competitorAnalysis":[],"menuAnalysis":{"strengths":[],"weaknesses":[],"trendAlignment":"","engineeringNotes":""},"beverageAnalysis":{"strengths":[],"weaknesses":[],"trendAlignment":"","programNotes":""},"guestSentiment":{"google":{"positiveThemes":[],"negativeThemes":[],"overallRating":0,"totalReviews":0,"emotionalWords":[],"transactionalWords":[]},"opentable":{"positiveThemes":[],"negativeThemes":[],"rating":0,"notes":""},"resy":{"notes":""},"social":{"positiveThemes":[],"negativeThemes":[],"notes":""},"combined":{"positiveThemes":[],"negativeThemes":[],"overallSentiment":""}},"socialProof":{"presenceScore":0,"followers":0,"engagementRate":"","postingFrequency":"","contentQuality":"","notes":"","posting_consistency":0,"engagement_rate":0,"content_quality":0,"guest_tagging":0,"hashtag_performance":0},"brandCoherence":{"websiteAlign":"","menuAlign":"","socialAlign":"","reviewAlign":"","overallNotes":"","_grid":{"website":{"Tone":"aligned|partial|fractured","Visual Identity":"aligned|partial|fractured","Key Message":"aligned|partial|fractured","Value Proposition":"aligned|partial|fractured","Personality":"aligned|partial|fractured"},"menu":{"Tone":"","Visual Identity":"","Key Message":"","Value Proposition":"","Personality":""},"instagram":{"Tone":"","Visual Identity":"","Key Message":"","Value Proposition":"","Personality":""},"reviews":{"Tone":"","Visual Identity":"","Key Message":"","Value Proposition":"","Personality":""},"physical_space":{"Tone":"","Visual Identity":"","Key Message":"","Value Proposition":"","Personality":""}}},"seasonality":{"overview":"","peakTimes":[],"slowTimes":[],"insights":[],"monthly":[{"month":"Jan","reviews":0},{"month":"Feb","reviews":0},{"month":"Mar","reviews":0},{"month":"Apr","reviews":0},{"month":"May","reviews":0},{"month":"Jun","reviews":0},{"month":"Jul","reviews":0},{"month":"Aug","reviews":0},{"month":"Sep","reviews":0},{"month":"Oct","reviews":0},{"month":"Nov","reviews":0},{"month":"Dec","reviews":0}]},"roadmap":{"immediate":[],"shortTerm":[],"mediumTerm":[]},"whatSuccessLooksLike":""}`;
        return callClaude([{ role: 'user', content: msg }], 16000, fileImages);
      });

      // Map scores
      const mapped = {};
      if (result.scores) {
        for (const [key, val] of Object.entries(result.scores)) {
          mapped[key] = { score: val.score || 0, rationale: val.rationale || '', confidence: val.confidence || 'Medium', evidence: val.evidence || 'Inferred', aiGenerated: true };
        }
      }
      onAuditComplete({ scores: mapped, ...result });
      setActiveStep(null);
    } catch (err) {
      setError(err.message);
      setActiveStep(null);
    }
  };

  // --- RENDER ---

  if (activeStep === 'findComps') return <ProgressAnimation steps={COMP_STEPS} currentStep={currentProgress} title={`Finding competitors in ${intake?.city || 'the area'}...`} subtitle="Scanning the local market" />;
  if (activeStep === 'review') return <CompetitorList
    competitors={foundCompetitors || []}
    savedCompetitors={savedComps}
    onApprove={(comps) => {
      if (onCompetitorsApproved) onCompetitorsApproved(comps);
      setActiveStep(null);
    }}
    onFindMore={(kept, notes) => findCompetitors(kept, notes)}
  />;
  if (activeStep === 'market') return <ProgressAnimation steps={MARKET_STEPS} currentStep={currentProgress} title="Researching market..." subtitle="Analyzing approved competitors and building benchmarks" />;
  if (activeStep === 'audit') return <ProgressAnimation steps={AUDIT_STEPS} currentStep={currentProgress} title={`Auditing ${intake?.conceptName || 'this concept'}...`} subtitle={marketData ? 'Scoring against market baseline' : 'Researching and scoring'} />;

  const hasMarketData = !!marketData;
  const hasAuditData = !!audit_aiResearch;
  const hasManualScores = Object.values(existingScores || {}).some(s => s?.score > 0);

  return (
    <div>
      {/* Compact 3-step row */}
      <div className="card p-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        {/* Step 1 */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${approvedCompetitors ? 'bg-brand-gold/20 text-brand-gold' : 'bg-neutral-800 text-neutral-500'}`}>1</span>
          <span className="text-xs text-neutral-400 truncate">{approvedCompetitors ? `✓ ${approvedCompetitors.length} comps` : 'Competitors'}</span>
          <button onClick={() => findCompetitors()} className={`text-[10px] px-2 py-1 rounded whitespace-nowrap ${approvedCompetitors ? 'text-neutral-500 hover:text-white' : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'}`}>
            {approvedCompetitors ? 'Re-run' : 'Find'}
          </button>
        </div>

        <div className="hidden sm:block w-px h-6 bg-neutral-800"></div>

        {/* Step 2 */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${hasMarketData ? 'bg-brand-gold/20 text-brand-gold' : 'bg-neutral-800 text-neutral-500'}`}>2</span>
          <span className="text-xs text-neutral-400 truncate">{hasMarketData ? '✓ Market' : 'Market Research'}</span>
          {approvedCompetitors && !hasMarketData && (
            <button onClick={() => researchMarket(approvedCompetitors)} className="text-[10px] px-2 py-1 rounded bg-neutral-800 text-neutral-300 hover:bg-neutral-700 whitespace-nowrap">Research</button>
          )}
          {hasMarketData && (
            <button onClick={() => researchMarket(approvedCompetitors)} className="text-[10px] px-2 py-1 rounded text-neutral-500 hover:text-white whitespace-nowrap">Re-run</button>
          )}
        </div>

        <div className="hidden sm:block w-px h-6 bg-neutral-800"></div>

        {/* Step 3 */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 bg-brand-gold/20 text-brand-gold`}>3</span>
          <span className="text-xs text-neutral-400 truncate">AI Audit</span>
          <button onClick={runAudit} className="text-[10px] px-3 py-1 rounded bg-brand-gold text-black font-semibold hover:brightness-110 whitespace-nowrap">
            {hasAuditData ? 'Re-run' : 'Run'}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2 mt-2">{error}</div>
      )}
    </div>
  );
}
